import { CheckCircle2, CreditCard } from 'lucide-react'

const formatDate = value => {
  if (!value) return null

  return new Date(value).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const PlanCardSelectionContent = ({
  cardsLoading = false,
  cards = [],
  selectedCardId = '',
  onSelectCard,
  selectedPlan,
  addCardContent = null,
  pendingCardMessage = '',
  errorMessage = ''
}) => {
  const basePrice =
    selectedPlan?.ufPriceWithoutTaxes != null ? Number(selectedPlan.ufPriceWithoutTaxes) : null

  return (
    <div className='grid gap-6 py-4 xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.3fr)]'>
      <div className='space-y-6'>
        <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
          <div className='max-w-xl'>
            <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
              Informacion importante
            </p>
            <ul className='mt-3 list-disc space-y-2 pl-5 text-sm font-sans leading-6 text-slate-600'>
              <li>
                Esta será la tarjeta con la que se cobrará automáticamente tu suscripción según el plan elegido.
              </li>
              <li>
                Para validarla, realizaremos un cargo menor a $50 que será devuelto automáticamente.
              </li>
              <li>
                No se realizará ningún cobro del plan en este paso.
              </li>
              <li>
                Tus pagos son procesados de forma segura a través de Mercado Pago.
              </li>
            </ul>
          </div>
        </div>

        {selectedPlan && (
          <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
            <div className='flex flex-col gap-2'>
              <div>
                <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.16em] text-primary/80'>
                  Plan seleccionado
                </p>
                <p className='mt-1 text-lg font-sans font-bold text-slate-800'>
                  {selectedPlan.name}
                </p>
              </div>

              {basePrice != null && (
                <div className='pt-2'>
                  <p className='text-sm font-sans font-semibold text-slate-800'>
                    {basePrice.toFixed(2)} UF + IVA
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {cardsLoading ? (
          <div className='flex items-center justify-center rounded-[24px] border border-slate-200 bg-white py-12 shadow-sm'>
            <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
          </div>
        ) : cards.length > 0 ? (
          <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
            <div className='grid gap-4'>
              {cards.map(card => {
                const isSelected = String(card.id) === String(selectedCardId)
                const savedDate = formatDate(card.createdAt)

                return (
                  <button
                    key={card.id}
                    type='button'
                    onClick={() => onSelectCard(String(card.id))}
                    className={`w-full rounded-[24px] border p-5 text-left font-sans shadow-sm transition-all ${
                      isSelected
                        ? 'border-accent2 bg-accent2/5 ring-2 ring-accent2/20'
                        : 'border-slate-200 bg-white hover:border-accent2/35 hover:shadow-md'
                    }`}
                  >
                    <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                      <div className='flex items-start gap-4'>
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-full ${
                            isSelected ? 'bg-accent2 text-white' : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <CreditCard className='h-5 w-5' />
                        </div>

                        <div>
                          <div className='flex flex-wrap items-center gap-2'>
                            <p className='text-lg font-sans font-bold text-slate-800'>
                              {card.name ||
                                `${card.cardType?.toUpperCase?.() || 'Tarjeta'} **** ${
                                  card.lastFourDigits
                                }`}
                            </p>
                            {isSelected && (
                              <span className='inline-flex items-center gap-1 rounded-full bg-accent2 px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] text-white'>
                                <CheckCircle2 className='h-3.5 w-3.5' />
                                Seleccionada
                              </span>
                            )}
                          </div>

                          <p className='mt-1 text-sm font-sans text-slate-600'>
                            {(card.cardType || 'Tarjeta').toUpperCase()} **** {card.lastFourDigits}
                          </p>

                          {savedDate && (
                            <p className='mt-1 text-xs font-sans text-slate-500'>
                              Guardada el {savedDate}
                            </p>
                          )}
                        </div>
                      </div>

                      <div
                        className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-sans font-semibold ${
                          isSelected
                            ? 'bg-accent2 text-white'
                            : 'border border-slate-200 bg-white text-slate-700'
                        }`}
                      >
                        {isSelected ? 'Lista para usar' : 'Usar esta tarjeta'}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>

      <div className='space-y-6 min-w-0'>
        {addCardContent}

        {pendingCardMessage && (
          <div className='rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-sans text-blue-800'>
            {pendingCardMessage}
          </div>
        )}

        {errorMessage && (
          <div className='rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-sans text-red-700'>
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanCardSelectionContent
